import type { RiverRushOtter, FoodItem, Obstacle, PowerUp, BiomeType } from '../types';

export const riverRushOtters: RiverRushOtter[] = [
  {
    id: 'sea-otter',
    name: 'Sea Otter',
    speciesId: 'sea-otter',
    ability: 'Tool Time',
    abilityDescription: 'Double-tap to use a rock to break obstacles!',
    unlockCondition: 'Available from start',
    unlocked: true,
  },
  {
    id: 'giant-otter',
    name: 'Giant Otter',
    speciesId: 'giant-otter',
    ability: 'Pack Call',
    abilityDescription: 'Double-tap to summon otter friends for protection!',
    unlockCondition: 'Travel 5,000m total',
    unlocked: false,
  },
  {
    id: 'eurasian-otter',
    name: 'Eurasian Otter',
    speciesId: 'eurasian-otter',
    ability: 'Night Vision',
    abilityDescription: 'See obstacles from further away!',
    unlockCondition: 'Collect 500 fish total',
    unlocked: false,
  },
  {
    id: 'asian-small-clawed',
    name: 'Asian Small-clawed',
    speciesId: 'asian-small-clawed-otter',
    ability: 'Nimble Paws',
    abilityDescription: 'Smaller hitbox - fit through tight gaps!',
    unlockCondition: 'Score 10,000 points in one game',
    unlocked: false,
  },
  {
    id: 'smooth-coated',
    name: 'Smooth-coated Otter',
    speciesId: 'smooth-coated-otter',
    ability: 'Speed Burst',
    abilityDescription: 'Move faster and more smoothly!',
    unlockCondition: 'Complete 10 runs',
    unlocked: false,
  },
  {
    id: 'river-otter',
    name: 'River Otter',
    speciesId: 'north-american-river-otter',
    ability: 'Versatile Diet',
    abilityDescription: 'All food gives equal points!',
    unlockCondition: 'Play 20 games',
    unlocked: false,
  },
];

export const foodItems: FoodItem[] = [
  // Kelp Forest foods
  { id: 'sea-urchin', name: 'Sea Urchin', points: 15, preferredBy: ['sea-otter'], biomes: ['kelp-forest'], sprite: 'urchin' },
  { id: 'crab', name: 'Crab', points: 10, preferredBy: ['sea-otter', 'giant-otter', 'asian-small-clawed'], biomes: ['kelp-forest', 'amazon-river', 'asian-mangroves'], sprite: 'crab' },
  { id: 'clam', name: 'Clam', points: 12, preferredBy: ['sea-otter'], biomes: ['kelp-forest'], sprite: 'clam' },
  { id: 'abalone', name: 'Abalone', points: 20, preferredBy: ['sea-otter'], biomes: ['kelp-forest'], sprite: 'clam' },

  // Amazon River foods
  { id: 'piranha', name: 'Piranha', points: 12, preferredBy: ['giant-otter'], biomes: ['amazon-river'], sprite: 'fish' },
  { id: 'catfish', name: 'Catfish', points: 15, preferredBy: ['giant-otter'], biomes: ['amazon-river'], sprite: 'fish' },

  // European Stream foods
  { id: 'trout', name: 'Trout', points: 12, preferredBy: ['eurasian-otter', 'river-otter'], biomes: ['european-stream'], sprite: 'fish' },
  { id: 'salmon', name: 'Salmon', points: 15, preferredBy: ['eurasian-otter', 'river-otter'], biomes: ['european-stream'], sprite: 'fish' },
  { id: 'frog', name: 'Frog', points: 8, preferredBy: ['eurasian-otter'], biomes: ['european-stream', 'african-wetlands'], sprite: 'frog' },
  { id: 'crayfish', name: 'Crayfish', points: 10, preferredBy: ['eurasian-otter', 'river-otter'], biomes: ['european-stream'], sprite: 'shrimp' },

  // Asian Mangroves foods
  { id: 'mudskipper', name: 'Mudskipper', points: 10, preferredBy: ['smooth-coated', 'asian-small-clawed'], biomes: ['asian-mangroves'], sprite: 'fish' },
  { id: 'shrimp', name: 'Shrimp', points: 8, preferredBy: ['asian-small-clawed', 'smooth-coated'], biomes: ['asian-mangroves'], sprite: 'shrimp' },
  { id: 'mollusk', name: 'Mollusk', points: 10, preferredBy: ['asian-small-clawed'], biomes: ['asian-mangroves'], sprite: 'clam' },

  // African Wetlands foods
  { id: 'cichlid', name: 'Cichlid', points: 12, preferredBy: [], biomes: ['african-wetlands'], sprite: 'fish' },
  { id: 'tilapia', name: 'Tilapia', points: 10, preferredBy: [], biomes: ['african-wetlands'], sprite: 'fish' },

  // Universal fish
  { id: 'small-fish', name: 'Small Fish', points: 5, preferredBy: [], biomes: ['kelp-forest', 'amazon-river', 'european-stream', 'asian-mangroves', 'african-wetlands'], sprite: 'fish' },
];

export const obstacles: Obstacle[] = [
  // Kelp Forest obstacles
  { id: 'shark', name: 'Shark', damage: 2, biomes: ['kelp-forest'], sprite: 'shark', canBreak: false },
  { id: 'orca', name: 'Orca', damage: 3, biomes: ['kelp-forest'], sprite: 'shark', canBreak: false },
  { id: 'fishing-net', name: 'Fishing Net', damage: 1, biomes: ['kelp-forest', 'amazon-river', 'european-stream'], sprite: 'net', canBreak: true },
  { id: 'boat', name: 'Boat', damage: 2, biomes: ['kelp-forest'], sprite: 'rock', canBreak: false },

  // Amazon River obstacles
  { id: 'caiman', name: 'Caiman', damage: 2, biomes: ['amazon-river'], sprite: 'shark', canBreak: false },
  { id: 'anaconda', name: 'Anaconda', damage: 2, biomes: ['amazon-river'], sprite: 'log', canBreak: false },
  { id: 'debris', name: 'Debris', damage: 1, biomes: ['amazon-river'], sprite: 'log', canBreak: true },

  // European Stream obstacles
  { id: 'rock', name: 'Rock', damage: 1, biomes: ['european-stream', 'african-wetlands'], sprite: 'rock', canBreak: true },
  { id: 'log', name: 'Log', damage: 1, biomes: ['european-stream', 'amazon-river'], sprite: 'log', canBreak: true },
  { id: 'heron', name: 'Heron', damage: 1, biomes: ['european-stream'], sprite: 'shark', canBreak: false },
  { id: 'culvert', name: 'Culvert', damage: 2, biomes: ['european-stream'], sprite: 'rock', canBreak: false },

  // Asian Mangroves obstacles
  { id: 'crocodile', name: 'Crocodile', damage: 2, biomes: ['asian-mangroves', 'african-wetlands'], sprite: 'shark', canBreak: false },
  { id: 'fishing-trap', name: 'Fishing Trap', damage: 1, biomes: ['asian-mangroves'], sprite: 'net', canBreak: true },
  { id: 'plastic', name: 'Plastic Waste', damage: 1, biomes: ['asian-mangroves', 'european-stream'], sprite: 'pollution', canBreak: true },
  { id: 'oil-slick', name: 'Oil Slick', damage: 1, biomes: ['asian-mangroves', 'kelp-forest'], sprite: 'pollution', canBreak: false },

  // African Wetlands obstacles
  { id: 'hippo', name: 'Hippo', damage: 3, biomes: ['african-wetlands'], sprite: 'rock', canBreak: false },
  { id: 'eagle', name: 'Eagle', damage: 1, biomes: ['african-wetlands'], sprite: 'shark', canBreak: false },
];

export const powerUps: PowerUp[] = [
  {
    id: 'raft-up',
    name: 'Raft Up!',
    description: 'Summon otter friends for wider collection area',
    duration: 10,
    sprite: 'raft',
    effect: 'raft',
  },
  {
    id: 'tool-time',
    name: 'Tool Time',
    description: 'Use a rock to smash through obstacles',
    duration: 8,
    sprite: 'tool',
    effect: 'tool',
  },
  {
    id: 'speed-splash',
    name: 'Speed Splash',
    description: 'Faster movement with invincibility',
    duration: 5,
    sprite: 'speed',
    effect: 'speed',
  },
  {
    id: 'mega-appetite',
    name: 'Mega Appetite',
    description: 'All food worth 3x points',
    duration: 12,
    sprite: 'appetite',
    effect: 'appetite',
  },
];

export interface BiomeConfig {
  id: BiomeType;
  name: string;
  description: string;
  backgroundColor: number;
  waterColor: number;
  foregroundColor: number;
  spawnRate: {
    food: number;
    obstacle: number;
    powerUp: number;
  };
}

export const biomes: BiomeConfig[] = [
  {
    id: 'kelp-forest',
    name: 'Kelp Forest',
    description: 'Pacific coastal waters with kelp and sea life',
    backgroundColor: 0x1a5276,
    waterColor: 0x2e86ab,
    foregroundColor: 0x1b4332,
    spawnRate: { food: 0.03, obstacle: 0.015, powerUp: 0.003 },
  },
  {
    id: 'amazon-river',
    name: 'Amazon River',
    description: 'Dense jungle rivers with murky waters',
    backgroundColor: 0x2d4a3e,
    waterColor: 0x5d7052,
    foregroundColor: 0x1a3a1a,
    spawnRate: { food: 0.035, obstacle: 0.018, powerUp: 0.003 },
  },
  {
    id: 'european-stream',
    name: 'European Stream',
    description: 'Peaceful countryside rivers',
    backgroundColor: 0x3498db,
    waterColor: 0x5dade2,
    foregroundColor: 0x27ae60,
    spawnRate: { food: 0.028, obstacle: 0.012, powerUp: 0.004 },
  },
  {
    id: 'asian-mangroves',
    name: 'Asian Mangroves',
    description: 'Tidal mangrove swamps',
    backgroundColor: 0x1e3d32,
    waterColor: 0x3d6b59,
    foregroundColor: 0x2d5a27,
    spawnRate: { food: 0.032, obstacle: 0.016, powerUp: 0.003 },
  },
  {
    id: 'african-wetlands',
    name: 'African Wetlands',
    description: 'Clear tropical lake waters',
    backgroundColor: 0x2980b9,
    waterColor: 0x48c9b0,
    foregroundColor: 0x7d6608,
    spawnRate: { food: 0.03, obstacle: 0.014, powerUp: 0.003 },
  },
];

export const otterFacts: string[] = [
  "Sea otters have the densest fur of any mammal - up to 1 million hairs per square inch!",
  "Giant otters are called 'river wolves' because of their fierce hunting ability.",
  "Otters hold hands while sleeping to keep from drifting apart!",
  "Sea otters use rocks as tools to crack open shellfish - one of the few animals to use tools!",
  "A group of otters is called a 'raft' when in water and a 'romp' on land.",
  "Otters can hold their breath for up to 8 minutes while diving for food.",
  "Baby otters are called pups, and they're born knowing how to float!",
  "Sea otters eat about 25% of their body weight in food every day.",
  "Giant otters have at least 22 different vocalizations to communicate.",
  "Each giant otter has a unique throat patch pattern, like a human fingerprint!",
  "Asian small-clawed otters are the smallest otter species, weighing just 2-5 kg.",
  "Eurasian otters can live at altitudes over 4,000 meters in the Himalayas!",
  "In Bangladesh, fishermen have trained smooth-coated otters to help catch fish for centuries.",
  "Marine otters are called 'chungungo' and have cat-like faces!",
  "Otters groom their fur constantly to maintain its waterproof and insulating properties.",
];

export function getRandomFact(): string {
  return otterFacts[Math.floor(Math.random() * otterFacts.length)];
}

export function getFoodForBiome(biome: BiomeType): FoodItem[] {
  return foodItems.filter(food => food.biomes.includes(biome));
}

export function getObstaclesForBiome(biome: BiomeType): Obstacle[] {
  return obstacles.filter(obs => obs.biomes.includes(biome));
}
